import React, { useCallback, useEffect, useState } from 'react';

import { link } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { ButtonBase, Paper, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link } from 'react-router-dom';
import { animated, config, useSpring } from 'react-spring';
import { Spacer } from 'src/components/generic';
import { EmptyInbox } from 'src/components/icons/EmptyInbox';
import {
  getWorkflowRuntimeString,
  WorkflowAssigneesChooser,
  WorkflowListStateChip,
  WorkflowPartiesList,
} from 'src/components/workflow';
import type { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick } from 'src/helpers';
import { useGetInboxWorkflows } from 'src/hooks';
import type { UserDefinedRoleSubset, UserSubset } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

import type { DocumentWithCommentsVersion, PhaseWithOrder } from './WorkflowTasks';
import {
  ActiveSigningGroup,
  useGetIsInboxComment,
  useGetIsInboxSignature,
  useGetIsInboxTask,
  useGetIsInboxUnusedAction,
  WorkflowFilesIssues,
  WorkflowIssues,
  WorkflowTasks,
} from './WorkflowTasks';

const useStyles = makeStyles()(theme => ({
  actionListBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  body: {
    flex: 1,
    overflow: 'auto',
  },
  checkbox: {
    padding: 0,
  },
  emptyContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(20, 2),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2, 0, 2),
  },
  main: {
    padding: theme.spacing(2),
  },
  partiesList: {
    display: 'flex',
  },
  root: {
    borderRadius: '6px',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: 'fit-content',
    marginBottom: theme.spacing(1),
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  sectionList: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    padding: theme.spacing(2, 0),
  },
  titleLink: {
    color: 'inherit',
    minWidth: 0,
    textDecoration: 'none',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(2, 0),
  },
  wrapperHeader: {
    display: 'flex',
    flexDirection: 'column',
  },
  wrapperTitle: {
    '&:hover': {
      color: theme.appColors.primary,
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    cursor: 'pointer',
  },
}));

export enum WorkflowInboxFilter {
  All = 'All',
  AssignedToMe = 'AssignedToMe',
}

export const workflowInboxFilterVar = makeVar<WorkflowInboxFilter>(WorkflowInboxFilter.All);

export function WorkflowInbox() {
  const { classes } = useStyles();

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h1">Inbox</Typography>
        <WorkflowActionSwitcher />
      </div>
      <div className={classes.main}>
        <WorkflowActions />
      </div>
    </Paper>
  );
}

export function useGetIsInboxWorkflow(): {
  getIsInboxWorkflow: (
    workflow: WorkflowInboxWorkflow,
    filter: WorkflowInboxFilter
  ) => { inboxActionCount: number; isInboxWorkflow: boolean };
} {
  const { getIsInboxTask } = useGetIsInboxTask();
  const { getIsInboxComment } = useGetIsInboxComment();
  const { getIsInboxSignature } = useGetIsInboxSignature();
  const { getIsInboxUnusedAction } = useGetIsInboxUnusedAction();
  const safeGetIsInboxWorkflow = useCallback(
    (
      workflow: WorkflowInboxWorkflow,
      filter: WorkflowInboxFilter
    ): { inboxActionCount: number; isInboxWorkflow: boolean } => {
      const inboxTasksCount = workflow.tasks.filter(task =>
        getIsInboxTask(task, workflow, filter)
      ).length;
      const inboxCommentsCount = workflow.files.filter(file =>
        file.document.comments.some(comment => getIsInboxComment(comment, workflow, filter))
      ).length;
      const inboxSignaturesCount =
        workflow.activeStep?.actions.filter(recipient =>
          getIsInboxSignature(workflow, recipient, filter)
        ).length ?? 0;
      const inboxUnusedActionsCount = workflow.unusedActions.filter(action =>
        getIsInboxUnusedAction(action, filter)
      ).length;
      const missingActionsCount = workflow.missingActions ? 1 : 0;
      const inboxActionCount =
        inboxTasksCount +
        inboxCommentsCount +
        inboxSignaturesCount +
        inboxUnusedActionsCount +
        missingActionsCount;

      return {
        inboxActionCount,
        isInboxWorkflow: inboxActionCount > 0,
      };
    },
    [getIsInboxComment, getIsInboxTask, getIsInboxSignature, getIsInboxUnusedAction]
  );

  return {
    getIsInboxWorkflow: safeGetIsInboxWorkflow,
  };
}

export const workflowsInboxScrollableTargetId = 'workflow-inbox-scroll';

export function WorkflowActions() {
  const { classes } = useStyles();

  const {
    fetchMoreWorkflows,
    hasMore,
    loading: workflowsLoading,
    workflows,
  } = useGetInboxWorkflows({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const handleFetchMore = useCallback(async () => {
    if (!workflowsLoading) await fetchMoreWorkflows();
  }, [fetchMoreWorkflows, workflowsLoading]);

  if (workflowsLoading && !workflows.length) return <InboxWorkflowsLoading />;

  if (!workflows.length)
    return (
      <div className={classes.emptyContainer}>
        <EmptyInbox />
        <div>
          <Typography align="center" variant="subtitle1">
            {"You're all caught up! 🪄"}
          </Typography>
          <Typography align="center" variant="body2">
            Nice work completing all workflow actions.
          </Typography>
        </div>
      </div>
    );

  return (
    <div className={classes.body}>
      <InfiniteScroll
        dataLength={workflows.length}
        hasMore={hasMore}
        loader={<InboxWorkflowsLoading />}
        next={handleFetchMore}
        scrollableTarget={workflowsInboxScrollableTargetId}
      >
        <WorkflowActionsList inboxWorkflows={workflows} />
      </InfiniteScroll>
    </div>
  );
}

function InboxWorkflowsLoading(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.body}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className={classes.wrapper}>
          <div className={classes.wrapperHeader}>
            <div className={classes.row}>
              <Typography variant="h2">
                <Skeleton variant="text" width={150} />
              </Typography>
              <Skeleton
                height={24}
                style={{ borderRadius: 100 }}
                variant="rectangular"
                width={100}
              />
            </div>
            <div className={classes.row}>
              <Skeleton variant="text" width={100} />
              <Spacer />
              <Skeleton height={32} variant="circular" width={32} />
              <Skeleton variant="text" width={120} />
            </div>
          </div>
          <div className={clsx(classes.actionListBody, classes.sectionList)}>
            <Skeleton height={40} variant="rectangular" />
            <Skeleton height={40} variant="rectangular" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function WorkflowActionsList({
  inboxWorkflows,
}: {
  inboxWorkflows: Apollo.GetWorkflowInboxQuery['workflowInboxWorkflows']['rows'];
}) {
  const { classes } = useStyles();

  return (
    <>
      {inboxWorkflows.map(workflow => (
        <div key={workflow.id} className={classes.wrapper}>
          <div className={classes.wrapperHeader}>
            <div className={classes.row}>
              <Link
                className={classes.titleLink}
                to={link.WorkflowDetail({ workflowId: workflow.id })}
                onClick={e =>
                  openLinkFromAnchorClick(e, link.WorkflowDetail({ workflowId: workflow.id }), {
                    title: workflow.name || 'Workflow',
                  })
                }
              >
                <Typography className={classes.wrapperTitle} variant="h2">
                  {workflow.name || 'Untitled'}
                </Typography>
              </Link>
              <WorkflowListStateChip workflow={workflow} />
            </div>
            <div className={classes.row}>
              <WorkflowPartiesList workflow={workflow} />
              <Spacer />
              <WorkflowAssigneesChooser workflow={workflow} />
              <Typography variant="body2">{`Started ${getWorkflowRuntimeString(workflow)} ago`}</Typography>
            </div>
          </div>
          <WorkflowActionsListBody className={classes.sectionList} workflow={workflow} />
        </div>
      ))}
    </>
  );
}

export interface WorkflowInboxWorkflow {
  __typename: Apollo.Workflow['__typename'];
  activeStep: {
    __typename: Apollo.WorkflowStep['__typename'];
    actions: Array<{
      __typename: Apollo.WorkflowRecipient['__typename'];
      assignee: UserSubset | null;
      assigneeRole: UserDefinedRoleSubset | null;
      completedAt: string | null;
      createdAt: string;
      firstSentAt: string | null;
      id: string;
      lastSentAt: string | null;
      type: Apollo.WorkflowRecipientType;
    }>;
    completedAt: string | null;
    createdAt: string;
    id: string;
    startedAt: string | null;
  } | null;
  completedAt: string | null;
  createdAt: string;
  files: Array<{
    __typename: Apollo.WorkflowFile['__typename'];
    document: {
      __typename: Apollo.Document['__typename'];
      comments: Array<{
        __typename: Apollo.Comment['__typename'];
        author: UserSubset | null;
        createdAt: string;
        deletedAt: string | null;
        id: string;
        nodeRefId: string | null;
        resolved: boolean;
      }>;
      createdAt: string;
      id: string;
      latestVersion: DocumentWithCommentsVersion;
      name: string;
      variant: Apollo.DocumentVariant;
    };
    id: string;
    orderId: string;
  }>;
  id: string;
  isSignedOutsideAline: boolean;
  missingActions: boolean;
  name: string;
  owner: UserSubset;
  parties: Pick<Apollo.Party, 'description' | 'id' | 'name' | '__typename'>[];
  permissions: WorkflowPermissionSubset[];
  phases: Array<PhaseWithOrder>;
  publishedAt: string | null;
  startedAt: string | null;
  state: Apollo.WorkflowState;
  status: Apollo.WorkflowStatus;
  tasks: Array<{
    __typename: Apollo.Task['__typename'];
    assignments: {
      permission: Pick<Apollo.WorkflowPermissionSimpleFragment, 'account' | 'id' | 'role'>;
    }[];
    author: UserSubset | null;
    completedAt: string | null;
    id: string;
    isDelegatedToAi: boolean;
    parentTask: { id: string } | null;
    phase: { id: string } | null;
    requesterDisallowedReason: {
      __typename: Apollo.DisallowedTaskReasons['__typename'];
      changeStatus: string | null;
      edit: string | null;
    };
    startedAt: string | null;
    status: Apollo.TaskStatus;
    title: string;
    updatedAt: string;
    variant: Apollo.TaskVariant;
  }>;
  unusedActions: Array<{
    __typename: Apollo.WorkflowRecipient['__typename'];
    assignee: UserSubset | null;
    assigneeRole: UserDefinedRoleSubset | null;
    completedAt: string | null;
    createdAt: string;
    firstSentAt: string | null;
    firstViewedAt: string | null;
    id: string;
    lastSentAt: string | null;
    lastViewedAt: string | null;
    startedAt: string | null;
    status: Apollo.WorkflowRecipientStatus;
    type: Apollo.WorkflowRecipientType;
  }>;
  variant: Apollo.WorkflowVariant;
}

export function WorkflowActionsListBody({
  className,
  onClose,
  openInNewTab = true,
  workflow,
}: {
  workflow: WorkflowInboxWorkflow;
  className?: string;
  onClose?: () => void;
  openInNewTab?: boolean;
}) {
  const { classes } = useStyles();

  return (
    <div className={clsx(classes.actionListBody, className)}>
      <ActiveSigningGroup workflow={workflow} onClose={onClose} />
      <WorkflowTasks openInNewTab={openInNewTab} workflow={workflow} onClose={onClose} />
      <WorkflowFilesIssues workflow={workflow} onClose={onClose} />
      <WorkflowIssues openInNewTab={openInNewTab} workflow={workflow} onClose={onClose} />
    </div>
  );
}

const useWorkflowActionSwitcherStyles = makeStyles()(theme => ({
  activeBackground: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '50%',
    zIndex: 0,
  },
  button: {
    '&.active': {
      color: theme.palette.common.white,
    },
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    position: 'relative',
    zIndex: 1,
  },
  buttonGroup: {
    border: theme.appBorders.outline,
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    maxHeight: 34,
    position: 'relative',
  },
}));

export function WorkflowActionSwitcher() {
  const { classes } = useWorkflowActionSwitcherStyles();
  const filter = useReactiveVar(workflowInboxFilterVar);
  const [dimensions, setDimensions] = useState({ left: 0, width: 0 });
  const [allActionsNode, setAllActionsNode] = useState<HTMLButtonElement | null>(null);
  const [assignedToMeNode, setAssignedToMeNode] = useState<HTMLButtonElement | null>(null);
  const springStyles = useSpring({
    config: config.stiff,
    left: dimensions.left,
    width: dimensions.width,
  });

  useEffect(() => {
    const activeButtonNode = filter === WorkflowInboxFilter.All ? allActionsNode : assignedToMeNode;

    setDimensions({
      left: activeButtonNode?.offsetLeft ?? 0,
      width: activeButtonNode?.offsetWidth ?? 0,
    });
  }, [allActionsNode, assignedToMeNode, filter]);

  return (
    <div className={classes.buttonGroup}>
      <animated.div
        className={classes.activeBackground}
        style={{
          ...springStyles,
          position: 'absolute',
        }}
      />
      <ButtonBase
        ref={setAllActionsNode}
        className={clsx(classes.button, filter === WorkflowInboxFilter.All && 'active')}
        onClick={() => workflowInboxFilterVar(WorkflowInboxFilter.All)}
      >
        <Typography color="inherit">All Actions</Typography>
      </ButtonBase>
      <ButtonBase
        ref={setAssignedToMeNode}
        className={clsx(classes.button, filter === WorkflowInboxFilter.AssignedToMe && 'active')}
        onClick={() => workflowInboxFilterVar(WorkflowInboxFilter.AssignedToMe)}
      >
        <Typography color="inherit">My Actions</Typography>
      </ButtonBase>
    </div>
  );
}
